import { motion } from 'framer-motion';
import { ArrowUpRight } from '@phosphor-icons/react';
import { PROJECTS } from '../data/site';
import useParallax from '../hooks/useParallax';
import Container from '../components/ui/Container';
import Chip from '../components/ui/Chip';
import LinkButton from '../components/ui/Button';
import Reveal from '../components/ui/Reveal';
import PageHeader from '../components/PageHeader';
import CtaBand from '../components/CtaBand';

function ProjectCase({ project, dark }) {
  const { ref, y } = useParallax(18);
  const label = dark ? 'text-white/50' : 'text-ink-muted';
  const body = dark ? 'text-white/75' : 'text-ink-soft';

  return (
    <Reveal as="article" from="up" amount={0.15}>
      <div
        className={`rounded-5xl p-4 sm:p-8 lg:p-10 ${
          dark ? 'bg-night-glow bg-night text-white' : 'bg-lavender text-ink'
        }`}
      >
        <motion.a
          ref={ref}
          style={{ y }}
          href={project.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${project.name} 사이트 열기`}
          className="group block rounded-2xl bg-white p-1.5 shadow-frame sm:rounded-3xl sm:p-2"
        >
          <div className="overflow-hidden rounded-xl sm:rounded-2xl">
            <img
              src={project.image}
              alt={project.alt}
              width={1600}
              height={1000}
              className="w-full transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            />
          </div>
        </motion.a>

        <div className="mt-8 grid gap-8 px-2 pb-2 sm:px-0 sm:pb-0 lg:mt-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <p className={`text-[14px] font-semibold ${dark ? 'text-sky' : 'text-accent'}`}>{project.type}</p>
            <h2 className="mt-2 text-[1.85rem] font-bold leading-[1.2] tracking-[-0.02em] sm:text-4xl">{project.name}</h2>
            <p className={`mt-4 text-base leading-[1.8] sm:text-lg ${body}`}>{project.summary}</p>
          </div>

          <div className="lg:col-span-6">
            <dl className="grid gap-6 sm:grid-cols-2">
              <div>
                <dt className={`text-[13px] font-semibold ${label}`}>진행 범위</dt>
                <dd className="mt-2 flex flex-wrap gap-2">
                  {project.scope.map((item) => (
                    <Chip key={item} tone={dark ? 'inverse' : 'accent'}>
                      {item}
                    </Chip>
                  ))}
                </dd>
              </div>
              <div>
                <dt className={`text-[13px] font-semibold ${label}`}>주요 구성</dt>
                <dd className="mt-2 flex flex-wrap gap-2">
                  {project.highlights.map((item) => (
                    <Chip key={item} tone={dark ? 'inverse' : 'default'}>
                      {item}
                    </Chip>
                  ))}
                </dd>
              </div>
            </dl>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <LinkButton href={project.url} variant={dark ? 'inverse' : 'primary'}>
                사이트 방문
                <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </LinkButton>
              <span className={`text-[14px] ${label}`}>{project.domain}</span>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function ProjectsPage() {
  return (
    <>
      <PageHeader
        title="기획부터 배포까지 직접 진행한 사이트"
        description="모두 실제로 운영 중인 사이트입니다. 화면을 누르면 새 창에서 바로 열립니다."
      />

      <section className="py-14 sm:py-20">
        <Container className="flex flex-col gap-10 sm:gap-14">
          {PROJECTS.map((project, index) => (
            <ProjectCase key={project.slug} project={project} dark={index % 2 === 1} />
          ))}
        </Container>
      </section>

      <CtaBand />
    </>
  );
}

export default ProjectsPage;
